import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomFrom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function parseSqrtLikeToNumber(raw) {
  if (raw == null) return NaN;
  const cleaned = String(raw)
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-");
  if (!cleaned) return NaN;

  let s = cleaned
    .replace(/\\\\sqrt/gi, "√")
    .replace(/\\sqrt/gi, "√")
    .replace(/sqrt/gi, "√")
    .replace(/√\{([^}]+)\}/g, "√($1)")
    .replace(/√(\d+(?:\.\d+)?)/g, "Math.sqrt($1)")
    .replace(/([+\-]?\d+(?:\.\d+)?)√(\d+(?:\.\d+)?)/g, "$1*Math.sqrt($2)");

  try {
    const val = Function(`"use strict"; return (${s});`)();
    return typeof val === "number" && isFinite(val) ? val : NaN;
  } catch {
    return NaN;
  }
}

function toLatexPreview(raw, label = "V") {
  if (!raw) return "";
  let s = String(raw)
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-")
    .replace(/\\\\sqrt/gi, "√")
    .replace(/\\sqrt/gi, "√")
    .replace(/sqrt/gi, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/([+\-]?\d*)√(\d+)/g, (m, a, b) => `${a === "+" ? "" : a}\\sqrt{${b}}`);
  return `${label} = ${s}`;
}

function formatCoeffSqrt3(coeff) {
  if (coeff === 1) return "\\sqrt{3}";
  return `${coeff}\\sqrt{3}`;
}

export function generateTask() {
  const baseKind = randomFrom(["square", "equilateral", "hexagon"]);
  const h = randInt(2, 12);

  if (baseKind === "square") {
    const a = randInt(2, 14);
    const volume = a * a * h;
    return {
      type: "RegularPrismVolumeTask",
      data: {
        baseKind,
        a,
        h,
        volume,
        hasSqrt: false,
      },
    };
  }

  const a = randomFrom([2, 4, 6, 8, 10, 12]);
  if (baseKind === "equilateral") {
    const coeff = (a * a * h) / 4;
    return {
      type: "RegularPrismVolumeTask",
      data: {
        baseKind,
        a,
        h,
        coeff,
        hasSqrt: true,
        volume: coeff * Math.sqrt(3),
      },
    };
  }

  const coeff = (3 * a * a * h) / 2;
  return {
    type: "RegularPrismVolumeTask",
    data: {
      baseKind,
      a,
      h,
      coeff,
      hasSqrt: true,
      volume: coeff * Math.sqrt(3),
    },
  };
}

export function renderTask({ data }) {
  const { baseKind, a, h } = data;
  const prismType =
    baseKind === "square"
      ? "czworokątny"
      : baseKind === "equilateral"
      ? "trójkątny"
      : "sześciokątny";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        Dany jest graniastosłup prawidłowy {prismType}.
      </p>
      <BlockMath
        math={`a=${a}\\,\\text{cm},\\quad H=${h}\\,\\text{cm}`}
      />
      <p>Oblicz objętość tego graniastosłupa.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  return (
    <SqrtInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="V"
      suffixLatex={String.raw`\text{cm}^3`}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer?.raw ?? userAnswer?.parsed ?? "";
  const parsed = parseSqrtLikeToNumber(raw);
  if (!Number.isFinite(parsed)) return { correct: false };
  return { correct: Math.abs(parsed - data.volume) < 0.01 };
}

export function renderAnswer({ data }) {
  if (!data.hasSqrt) {
    return <InlineMath math={`V=${data.volume}\\,\\text{cm}^3`} />;
  }
  return <InlineMath math={`V=${formatCoeffSqrt3(data.coeff)}\\,\\text{cm}^3`} />;
}

export function renderUserAnswer(userAnswer) {
  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer?.raw ?? userAnswer?.parsed ?? "";
  if (!raw || String(raw).trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${toLatexPreview(raw, "V")}\\,\\text{cm}^3`} />;
}

export function renderExplanation({ data }) {
  const { baseKind, a, h } = data;

  if (baseKind === "square") {
    const pp = a * a;
    const v = data.volume;
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p>
          <strong>Krok 1:</strong> Liczymy pole podstawy (kwadrat):
        </p>
        <p>
          W graniastosłupie prawidłowym czworokątnym podstawa jest kwadratem.
        </p>
        <BlockMath math={`P_p=a^2=${a}^2=${pp}\\,\\text{cm}^2`} />
        <p>
          <strong>Krok 2:</strong> Korzystamy ze wzoru na objętość:
        </p>
        <BlockMath math={`V=P_p\\cdot H=${pp}\\cdot ${h}=${v}\\,\\text{cm}^3`} />
      </div>
    );
  }

  if (baseKind === "equilateral") {
    const coeff = data.coeff;
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p>
          <strong>Krok 1:</strong> Liczymy pole podstawy (trójkąt równoboczny):
        </p>
        <p>
          W graniastosłupie prawidłowym trójkątnym podstawa jest trójkątem równobocznym.
        </p>
        <BlockMath
          math={`P_p=\\frac{a^2\\sqrt{3}}{4}=\\frac{${a}^2\\sqrt{3}}{4}=${(a * a) / 4}\\sqrt{3}\\,\\text{cm}^2`}
        />
        <p>
          <strong>Krok 2:</strong> Korzystamy ze wzoru na objętość:
        </p>
        <BlockMath
          math={`V=P_p\\cdot H=\\left(${(a * a) / 4}\\sqrt{3}\\right)\\cdot ${h}=${formatCoeffSqrt3(coeff)}\\,\\text{cm}^3`}
        />
      </div>
    );
  }

  const coeff = data.coeff;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Liczymy pole podstawy (sześciokąt foremny):
      </p>
      <p>
        W graniastosłupie prawidłowym sześciokątnym podstawa jest sześciokątem foremnym.
      </p>
      <p>
        Sześciokąt foremny składa się z 6 trójkątów równobocznych o boku{" "}
        <InlineMath math="a" />, więc:
      </p>
      <BlockMath
        math={`P_p=6\\cdot\\frac{a^2\\sqrt{3}}{4}=\\frac{3a^2\\sqrt{3}}{2}=\\frac{3\\cdot ${a}^2\\sqrt{3}}{2}=${(3 * a * a) / 2}\\sqrt{3}\\,\\text{cm}^2`}
      />
      <p>
        <strong>Krok 2:</strong> Korzystamy ze wzoru na objętość:
      </p>
      <BlockMath
        math={`V=P_p\\cdot H=\\left(${(3 * a * a) / 2}\\sqrt{3}\\right)\\cdot ${h}=${formatCoeffSqrt3(coeff)}\\,\\text{cm}^3`}
      />
    </div>
  );
}
